import { resolveLayerAtTime } from "@/lib/kinema/core/effects/resolveLayer";
import { pathPointsToSVG } from "@/lib/kinema/core/tools/pathUtils";
import type { BaseLayerData } from "@/lib/kinema/core/types";
import { CANVAS, TRIM, type MailPieceSize } from "./mailPieceTemplates";
import { isInstalledFont, substituteFont } from "./lobRenderer";

/*
    Designing a postcard in Kinema and printing it (MPN-1775).

    Rachel: "Ideally we could use the Kinema editor we use for image/video templates to design
    the postcard, and then output the HTML". This is that. A composition goes in, postcard
    artwork comes out, and the text stays text -- which is the whole reason to do it this way
    rather than flattening the design to a picture. Lob renders real text at print resolution
    and substitutes {{first_name}} per person at print time; a flattened image can do neither.

    WHY THE OUTPUT IS SVG AND NOT DIVS. Kinema positions every layer by an anchor point and
    then rotates and scales about it, which is a CSS transform, and Lob's renderer ignores CSS
    transforms completely -- a rotated box comes back square, a scaled one comes back its
    original size, with no error. SVG transforms it honours. All of this was established by
    rendering probe cards through Lob's test key and looking at what came back:

      works: <g transform="translate/rotate/scale">, <rect rx>, <ellipse>, <path>, <text>,
             <foreignObject> with auto-wrapping HTML -- including inside a rotated group
      works: <image xlink:href="..."> with an https URL or a data: URI
      NOT:   <image href="..."> -- the modern spelling renders nothing at all
      NOT:   any CSS transform, flexbox, or the inset shorthand

    Text uses foreignObject rather than <text> so wrapping stays the browser's job. Kinema
    wraps on word boundaries inside the layer's own box, and reproducing that against SVG's
    non-wrapping <text> would mean measuring glyphs and hoping the printer's metrics agree
    with ours.

    A composition is a movie and a postcard is one frame, so conversion takes a single moment
    (the playhead by default) and flattens it. Anything that only exists as motion is gone.
    That, and every other thing that does not survive the trip, comes back in `warnings`
    rather than being dropped in silence -- silence is how this whole channel goes wrong.
*/

export interface KinemaComposition {
    layers: Record<string, BaseLayerData>;
    rootLayerId: string;
    playheadTime?: number;
}

export interface ConversionWarning {
    /** Short label, e.g. "Font substituted". */
    what: string;
    /** What it means for the printed card, in plain words. */
    detail: string;
}

export interface ConversionResult {
    html: string;
    warnings: ConversionWarning[];
}

/** Fetches the `@font-face` CSS for one variant, or null when there is none to be had. */
export type FontCssLoader = (
    family: string, weight: number, italic: boolean,
) => Promise<string | null>;

/**
 * Gather the font faces this design needs so they can travel with the artwork.
 *
 * Only for families the printer does not already have: asking for a face for Helvetica
 * would be a pointless round trip and a pointless kilobyte on every card.
 *
 * One face per family, taken from the heaviest weight in use. The renderer synthesises the
 * rest, and shipping a file per weight would multiply the size of the artwork for a
 * difference measured in hairlines.
 *
 * Anything that fails to load is simply absent from the map, which is what makes the
 * substitution path the safety net rather than the plan.
 */
export async function collectFontFaces(
    comp: KinemaComposition, load: FontCssLoader,
): Promise<Map<string, string>> {
    const heaviest = new Map<string, { weight: number; italic: boolean }>();

    for (const l of Object.values(comp?.layers ?? {})) {
        if (l.type !== "text") continue;
        const family = (l.fontFamily || "").trim();
        if (!family || isInstalledFont(family)) continue;

        const weight = Number(l.fontWeight) || 400;
        const italic = l.fontStyle === "italic";
        const current = heaviest.get(family);
        if (!current || weight > current.weight) heaviest.set(family, { weight, italic });
    }

    const faces = new Map<string, string>();
    await Promise.all([...heaviest.entries()].map(async ([family, v]) => {
        try {
            const css = await load(family, v.weight, v.italic);
            if (css && css.includes("@font-face")) faces.set(family, css);
        } catch {
            // Left out on purpose: the conversion falls back and says so.
        }
    }));
    return faces;
}

const escapeText = (s: string) =>
    String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escapeAttr = (s: string) => escapeText(s).replace(/"/g, "&quot;");

/** Trim a float to something readable in the emitted markup. */
const n = (v: number) => (Math.round(v * 1000) / 1000).toString();

/**
 * The transform that reproduces Kinema's placement.
 *
 * Konva puts the layer's ANCHOR at (x, y) and rotates and scales about it, so x/y is not the
 * top-left corner. Getting this wrong shifts every rotated or scaled layer by half its size,
 * which looks like a plausible design rather than a bug.
 */
function layerTransform(l: BaseLayerData): string {
    const parts = [`translate(${n(l.x)} ${n(l.y)})`];
    if (l.rotation) parts.push(`rotate(${n(l.rotation)})`);
    if (l.scaleX !== 1 || l.scaleY !== 1) parts.push(`scale(${n(l.scaleX)} ${n(l.scaleY)})`);
    if (l.anchorX || l.anchorY) parts.push(`translate(${n(-l.anchorX)} ${n(-l.anchorY)})`);
    return parts.join(" ");
}

function textLayer(
    l: BaseLayerData,
    warn: (w: ConversionWarning) => void,
    embeddedFonts: Set<string>,
): string {
    const asked = (l.fontFamily || "").trim();

    /*
        A font the artwork carries with it beats one the printer happens to own.

        The renderer only has the web-safe set installed, but it will fetch an @font-face it
        is given, so a design set in Inter prints in Inter rather than in a substitute. Only
        when no face could be fetched does it fall back -- and then it says so.
    */
    const embedded = asked && embeddedFonts.has(asked);
    const font = embedded
        ? { family: asked, substituted: false, requested: asked }
        : substituteFont(asked);

    if (font.substituted) {
        warn({
            what: `Font changed: ${font.requested} to ${font.family}`,
            detail: `The printer has no ${font.requested} and no copy of it could be fetched, `
                + `so the closest available font is used. Left alone it would have printed in `
                + `a serif face with nothing to say why.`,
        });
    }

    // display:table-cell, because the renderer has no flexbox and this is the one vertical
    // centring that predates it.
    const valign = l.verticalAlign === "middle" ? "middle"
        : l.verticalAlign === "bottom" ? "bottom" : "top";

    const style = [
        `font-family:'${font.family}',Helvetica,Arial,sans-serif`,
        `font-size:${n(l.fontSize)}px`,
        `font-weight:${l.fontWeight ?? 400}`,
        l.fontStyle === "italic" ? "font-style:italic" : "",
        `color:${l.color ?? "#000"}`,
        `text-align:${l.textAlign ?? "left"}`,
        `line-height:${l.lineHeight ?? 1.2}`,
        "display:table-cell",
        `vertical-align:${valign}`,
        `width:${n(l.width)}px`,
        `height:${n(l.height)}px`,
        "overflow:hidden",
    ].filter(Boolean).join(";");

    /*
        Explicit line breaks become <br/>.

        Kinema treats a newline in the text as a break and wraps on top of that; HTML
        collapses it to a space. Left alone, a headline the designer broke deliberately
        reflows into whatever the box width decides, which is a different design.
    */
    const markup = String(l.text ?? "").split("\n").map(escapeText).join("<br/>");

    return `<foreignObject x="0" y="0" width="${n(l.width)}" height="${n(l.height)}">`
        + `<div xmlns="http://www.w3.org/1999/xhtml" style="${escapeAttr(style)}">`
        + `${markup}</div></foreignObject>`;
}

function shapeLayer(l: BaseLayerData): string {
    const paint = `fill="${escapeAttr(l.fill ?? "#ffffff")}"`
        + (l.stroke && l.stroke !== "transparent" && l.strokeWidth
            ? ` stroke="${escapeAttr(l.stroke)}" stroke-width="${n(l.strokeWidth)}"` : "");

    if (l.shape === "ellipse") {
        const rx = l.width / 2;
        const ry = l.height / 2;
        return `<ellipse cx="${n(rx)}" cy="${n(ry)}" rx="${n(rx)}" ry="${n(ry)}" ${paint}/>`;
    }
    if (l.shape === "path" && Array.isArray(l.pathPoints) && l.pathPoints.length) {
        // The editor's own path serialiser, so a curve prints the way it was drawn.
        const d = pathPointsToSVG(l.pathPoints, Boolean(l.pathClosed));
        const fill = l.pathClosed ? paint : `fill="none" stroke="${escapeAttr(l.stroke || l.fill
            || "#000")}" stroke-width="${n(l.strokeWidth || 1)}"`;
        return `<path d="${escapeAttr(d)}" ${fill}/>`;
    }
    const rx = l.cornerRadius ? ` rx="${n(l.cornerRadius)}"` : "";
    return `<rect x="0" y="0" width="${n(l.width)}" height="${n(l.height)}"${rx} ${paint}/>`;
}

function imageLayer(l: BaseLayerData, warn: (w: ConversionWarning) => void): string {
    if (!l.src) return "";
    if (l.src.startsWith("s3://")) {
        warn({
            what: `Image "${l.name}" could not be read`,
            detail: "Its address came back unsigned, so the printer cannot fetch it. Reopen the "
                + "design in Kinema and import again.",
        });
        return "";
    }
    // xlink:href, not href. The modern spelling renders nothing on this engine, silently.
    // preserveAspectRatio="none" matches Kinema, whose renderer stretches to the layer box
    // and ignores objectFit entirely.
    return `<image xlink:href="${escapeAttr(l.src)}" x="0" y="0" `
        + `width="${n(l.width)}" height="${n(l.height)}" preserveAspectRatio="none"/>`;
}

/** Children of a parent, in paint order: lowest `order` first, so the highest ends on top. */
function childrenOf(comp: KinemaComposition, parentId: string): BaseLayerData[] {
    return Object.values(comp.layers)
        .filter((l) => l.parentId === parentId && l.id !== comp.rootLayerId)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function renderLayers(
    comp: KinemaComposition, parentId: string, time: number,
    warn: (w: ConversionWarning) => void, seenAnimation: { any: boolean },
    embeddedFonts: Set<string>, videoStill?: string,
): string {
    return childrenOf(comp, parentId).map((raw) => {
        if (raw.visible === false) return "";
        if (time < raw.startTime || time > raw.endTime) return "";

        if (Object.keys(raw.effects ?? {}).length) seenAnimation.any = true;
        const l = resolveLayerAtTime(raw, time);

        let body = "";
        switch (l.type) {
            case "text": body = textLayer(l, warn, embeddedFonts); break;
            case "shape": body = shapeLayer(l); break;
            case "image": body = imageLayer(l, warn); break;
            case "composition":
                body = renderLayers(comp, l.id, time, warn, seenAnimation, embeddedFonts,
                                    videoStill);
                break;
            case "video":
                /*
                    A card is one frame, so the question is which one.

                    The ad's cover is already the answer: it is chosen by walking backwards
                    from the end of the video until a frame is not black, which is the frame
                    that carries the CTA. Without it a video design imported as nothing at
                    all -- a white card and a warning -- which is not what somebody who
                    picked that design was asking for.
                */
                if (videoStill) {
                    body = `<image xlink:href="${escapeAttr(videoStill)}" x="0" y="0" `
                        + `width="${n(l.width)}" height="${n(l.height)}" `
                        + `preserveAspectRatio="none"/>`;
                    warn({
                        what: `Video layer "${l.name}" became a still`,
                        detail: "A postcard is one frame, so the video's cover was used: the "
                            + "last frame of it that is not black.",
                    });
                    break;
                }
                warn({
                    what: `Video layer "${l.name}" was left out`,
                    detail: "A postcard cannot carry video, and this design has no cover "
                        + "image to stand in for it.",
                });
                return "";
            case "audio":
                return "";   // never visible; nothing to say about it
            default:
                warn({
                    what: `Unsupported layer "${l.name}" (${l.type})`,
                    detail: "It was left out, because nothing here knows how to print it.",
                });
                return "";
        }
        if (!body) return "";

        if (l.shadowBlur || l.shadowOpacity) {
            warn({
                what: `Drop shadow on "${l.name}" was dropped`,
                detail: "Shadows are not carried across yet. The layer prints flat.",
            });
        }

        const opacity = typeof l.opacity === "number" && l.opacity < 1
            ? ` opacity="${n(l.opacity)}"` : "";
        return `<g transform="${escapeAttr(layerTransform(l))}"${opacity}>${body}</g>`;
    }).join("\n      ");
}

/**
 * Turn a Kinema composition into postcard artwork.
 *
 * `time` picks the frame; it defaults to wherever the playhead was left, which is the frame
 * the designer was last looking at.
 */
export function compositionToPostcardHtml(
    comp: KinemaComposition,
    opts: {
        size: MailPieceSize;
        time?: number;
        /**
         * A still to stand in for video layers, which a card cannot carry.
         *
         * The design's cover: the last frame of the video that is not black, which is where
         * the CTA card sits. Without one a video design converts to an empty card.
         */
        videoStill?: string;
        /**
         * How a design that is not the card's shape is placed.
         *
         * "fill" crops to the edges, which is what full bleed means and what a postcard
         * usually wants. "fit" keeps every pixel of the design and pads the rest with its
         * background, for when losing the edges is the worse outcome.
         */
        fit?: "fill" | "fit";
        /** `@font-face` rules to travel with the artwork, keyed by family name. */
        fontFaces?: Map<string, string>;
    },
): ConversionResult {
    const warnings: ConversionWarning[] = [];
    const warn = (w: ConversionWarning) => {
        // One of each: a font used on nine layers is one decision, not nine problems.
        if (!warnings.some((existing) => existing.what === w.what)) warnings.push(w);
    };

    const root = comp?.layers?.[comp?.rootLayerId];
    if (!root) {
        return {
            html: "",
            warnings: [{
                what: "Nothing to import",
                detail: "That design has no composition saved yet. Open it in Kinema and save.",
            }],
        };
    }

    const time = opts.time ?? comp.playheadTime ?? 0;
    const fit = opts.fit ?? "fill";
    const fontFaces = opts.fontFaces ?? new Map<string, string>();
    const embeddedFonts = new Set(fontFaces.keys());
    const seenAnimation = { any: false };
    const card = CANVAS[opts.size];
    const compAspect = root.width / root.height;
    const cardAspect = card.width / card.height;

    const mismatched = Math.abs(compAspect - cardAspect) > 0.02;
    if (mismatched && fit === "fill") {
        const cropped = compAspect > cardAspect ? "sides" : "top and bottom";
        warn({
            what: "The design is not the shape of the card",
            detail: `It is ${root.width}x${root.height} and a ${opts.size} card is `
                + `${card.width}x${card.height} inches, so filling it crops the ${cropped}. `
                + `Switch to Fit to keep the whole design, or design at `
                + `${Math.round(card.width * 300)}x${Math.round(card.height * 300)} to fill `
                + `the card with nothing lost.`,
        });
    } else if (mismatched) {
        warn({
            what: "The design is padded to the shape of the card",
            detail: `Nothing is cropped, so there is a band of background on two sides. `
                + `Design at ${Math.round(card.width * 300)}x`
                + `${Math.round(card.height * 300)} to fill the card edge to edge.`,
        });
    }

    const body = renderLayers(comp, comp.rootLayerId, time, warn, seenAnimation, embeddedFonts,
                              opts.videoStill);

    if (seenAnimation.any) {
        warn({
            what: "Animation was flattened",
            detail: "A postcard is one frame. This is the design as it looks at the playhead; "
                + "move the playhead and import again to choose a different moment.",
        });
    }

    const background = root.background
        ? `<rect x="0" y="0" width="${n(root.width)}" height="${n(root.height)}" `
          + `fill="${escapeAttr(root.background)}"/>\n      `
        : "";

    // preserveAspectRatio="slice" = cover: fill the card and crop the overflow, which is what
    // full bleed means. Anything short of filling it leaves white paper at an edge.
    const html = `<html>
<head>
<style>
  @page { margin: 0; }
  html, body { margin: 0; padding: 0; }
  /* Full bleed. Lob trims ${TRIM}in off every edge to reach ${opts.size}. */
  .card {
    width: ${card.width}in; height: ${card.height}in; overflow: hidden;
    background: ${escapeAttr(root.background || "#ffffff")};
  }
${[...fontFaces.values()].join("\n")}
</style>
</head>
<body>
  <div class="card">
    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
         width="${card.width}in" height="${card.height}in"
         viewBox="0 0 ${n(root.width)} ${n(root.height)}"
         preserveAspectRatio="xMidYMid ${fit === "fit" ? "meet" : "slice"}">
      ${background}${body}
    </svg>
  </div>
</body>
</html>`;

    return { html, warnings };
}
